import React from "react";
import styled from "styled-components";
import { Banner } from "@cinesys/ui/components";

const SIGN_IN_URL = "https://app.iconik.io";

// "unauthenticated" now covers both "never had a session" (initial load, no/invalid launch
// token) and "had one, it died mid-use" (see `useInterfaceAccess`'s `SESSION_EXPIRED_EVENT`
// handling) — same recovery either way (sign back in at iconik), so one message names both
// without guessing which one happened. "not-enabled" is a different situation for the user (a
// working session, just no entitlement) — no session-y language, no sign-in link, points at their
// admin instead since re-signing in wouldn't fix it.
const UNAUTHENTICATED_MESSAGE =
  "Your session has expired or could not be verified.";
const NOT_ENABLED_MESSAGE =
  "This interface isn't enabled for your organization. Contact your admin for access.";
// "not-found"/"error" are content-level, not access-level — used by `FeatureGate` for a query
// whose failure has nothing to do with the viewer's session (e.g. a stale/deleted asset link, or
// a real backend error). Neither implies re-signing in would help, so neither gets the link.
const NOT_FOUND_MESSAGE =
  "We couldn't find what you're looking for. It may have been moved or deleted.";
const ERROR_MESSAGE = "Something went wrong loading this. Please try again.";

const Center = styled.div`
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px;
  text-align: center;
`;

const SignInLink = styled.a`
  color: var(--blue300);
  font-size: 15px;
  font-weight: 600;
  text-decoration: underline;

  &:hover {
    color: var(--blue400);
  }
`;

export interface InterfaceAccessNoticeProps {
  reason: "unauthenticated" | "not-enabled" | "not-found" | "error";
}

const MESSAGE_BY_REASON: Record<InterfaceAccessNoticeProps["reason"], string> =
  {
    unauthenticated: UNAUTHENTICATED_MESSAGE,
    "not-enabled": NOT_ENABLED_MESSAGE,
    "not-found": NOT_FOUND_MESSAGE,
    error: ERROR_MESSAGE,
  };

/**
 * The centered, in-shell message for a denied/failed state — replaces a screen's real content
 * but leaves the header's placeholder chrome in place (Addison: stop blowing the whole page away
 * for this, the old full-screen-error behavior). `unauthenticated`/`not-enabled` come from
 * `useInterfaceAccess`; `not-found`/`error` come from `FeatureGate` gating a content query
 * instead (see its own doc comment). Copy is deliberately generic — none of the four name the
 * interface or the underlying resource, same "don't give away what the tool is" reasoning as the
 * placeholders this sits under. Only `unauthenticated` offers the sign-in link — it's the only
 * reason re-authenticating could actually fix.
 */
const InterfaceAccessNotice = ({ reason }: InterfaceAccessNoticeProps) => (
  <Center>
    <Banner error={MESSAGE_BY_REASON[reason]} />
    {reason === "unauthenticated" && (
      <SignInLink href={SIGN_IN_URL}>Sign in at iconik</SignInLink>
    )}
  </Center>
);

export default InterfaceAccessNotice;
